import React, { FC, useState } from 'react';
import dynamic from 'next/dynamic';
import { useTranslation, useExperiments } from '@wix/fe-essentials-standalone';
import { Box, TextButton, Text } from '@wix/design-system';
import { ChevronDown, ChevronUp, FlagSmall } from '@wix/wix-ui-icons-common';
import GoodToKnow from '../GoodToKnow';
import SingleUseLicense from '../SingleUseLicense';
import { useTemplateBi } from '@components/Hooks/useTemplateBi';
import { useHeaderContext } from '@components/Contexts/HeaderContext';
import ReportTemplateModalSuccessMsg from '../ReportTemplateModalSuccessMsg';
import { ENABLE_REPORT_TEMPLATE } from '@utils/constants';
import { useReportTemplateForm } from '@components/Hooks/useReportTemplate';
import {
  ACTIONS,
  useTemplateContext,
} from '@components/Contexts/TemplateContext';

type AdditionalInfoDesktopProps = {
  hasAdditionalPurchaseApps: boolean;
  templateName: string;
  listingId: string;
};

const ADDITIONAL_INFO_KEYS = {
  SINGLE_USE_LICENSE: 'singleUseLicense',
  GOOD_TO_KNOW: 'goodToKnow',
} as const;

const DynamicReportTemplateModal = dynamic(
  () => import('../ReportTemplateModal'),
  {
    loading: () => null,
    ssr: false,
  },
);

const AdditionalInfoDesktop: FC<AdditionalInfoDesktopProps> = ({
  hasAdditionalPurchaseApps,
  templateName,
  listingId,
}) => {
  const { onClose, onSubmit, openReportModal } = useReportTemplateForm({
    listingId,
  });
  const { experiments } = useExperiments({ readOnly: true });
  const showReportTemplateEnable = experiments.enabled(ENABLE_REPORT_TEMPLATE);
  const { userDetails } = useHeaderContext();
  const {
    dispatch,
    reportTemplateData: {
      showReportTemplateSuccessMsg,
      openReportTemplateModal,
    },
  } = useTemplateContext();
  const { logBiClick } = useTemplateBi({ is_mobile: false });

  const [openSingleUseLicense, setOpenSingleUseLicense] = useState(false);
  const [openGoodToKnow, setOpenGoodToKnow] = useState(false);

  const handleToggleCollapse = (
    key: (typeof ADDITIONAL_INFO_KEYS)[keyof typeof ADDITIONAL_INFO_KEYS],
  ) => {
    switch (key) {
      case ADDITIONAL_INFO_KEYS.SINGLE_USE_LICENSE:
        setOpenSingleUseLicense((prev) => !prev);
        break;
      case ADDITIONAL_INFO_KEYS.GOOD_TO_KNOW:
        setOpenGoodToKnow((prev) => !prev);
        break;
      default:
        break;
    }
  };

  const { t } = useTranslation();
  return (
    <Box
      gap="18px"
      direction="vertical"
      align="left"
      dataHook="additional-info-box"
    >
      <Box direction="vertical" gap="6px">
        <TextButton
          aria-expanded={openSingleUseLicense}
          size="small"
          dataHook="single-use-license-cta"
          skin="dark"
          onClick={() => {
            !openSingleUseLicense &&
              logBiClick({ clickType: 'Single use license' });
            handleToggleCollapse(ADDITIONAL_INFO_KEYS.SINGLE_USE_LICENSE);
          }}
          suffixIcon={openSingleUseLicense ? <ChevronUp /> : <ChevronDown />}
        >
          {t('marketplace.template-page.single-use-license-title')}
        </TextButton>
        {openSingleUseLicense && (
          <Box maxWidth="299px">
            <SingleUseLicense isMobileView={false} textSize="tiny" />
          </Box>
        )}
      </Box>
      <Box direction="vertical" display="flex">
        <TextButton
          aria-expanded={openGoodToKnow}
          size="small"
          dataHook="good-to-know-cta"
          skin="dark"
          onClick={() => {
            !openGoodToKnow && logBiClick({ clickType: 'good to know' });
            handleToggleCollapse(ADDITIONAL_INFO_KEYS.GOOD_TO_KNOW);
          }}
          suffixIcon={openGoodToKnow ? <ChevronUp /> : <ChevronDown />}
        >
          {t('marketplace.template-page.good-to-know-title')}
        </TextButton>
        {openGoodToKnow && (
          <Box
            maxWidth="299px"
            gap="15px"
            direction="vertical"
            paddingTop="6px"
          >
            <GoodToKnow
              textSize="tiny"
              hasAdditionalPurchaseApps={hasAdditionalPurchaseApps}
            />
          </Box>
        )}
      </Box>
      {userDetails?.email && showReportTemplateEnable && (
        <TextButton
          dataHook="report-template-cta"
          skin="dark"
          onClick={openReportModal}
        >
          <Box verticalAlign="middle" gap="6px">
            <FlagSmall color="#868AA5" />
            <Text size="tiny" secondary>
              {t('marketplace.template-page.report-template-cta')}
            </Text>
          </Box>
        </TextButton>
      )}
      {showReportTemplateSuccessMsg && (
        <ReportTemplateModalSuccessMsg
          isOpen={showReportTemplateSuccessMsg}
          onClose={() =>
            dispatch({
              type: ACTIONS.SET_REPORT_TEMPLATE_DATA,
              payload: { showReportTemplateSuccessMsg: false },
            })
          }
        />
      )}
      {openReportTemplateModal && (
        <DynamicReportTemplateModal
          onSubmit={onSubmit}
          isOpen={openReportTemplateModal}
          onClose={onClose}
          templateName={templateName}
        />
      )}
    </Box>
  );
};

export default AdditionalInfoDesktop;
